import {useCallback, useMemo} from 'react';
import {type UseFormReturn} from 'react-hook-form';
import {atom, useSetAtom} from 'jotai';
import {useAtomCallback} from 'jotai/utils';
import {focusAtom} from 'jotai-optics';
import {useAttributeProvider} from '@netx/core-attribute/context';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {type IAttributeTemplate} from '../schema';
import type {AttributeAction, AttributeValue} from '../types';

// RULES:
// explicitely clearing a value is a "null"

const getValidActions = (
	actions: Record<string, AttributeAction>,
	attributeTemplates: Record<number, IAttributeTemplate>,
) => {
	return Object.entries(actions).reduce((accum, [key, action]) => {
		return {
			...accum,
			[key]:
				action === 'clear'
					? attributeTemplates[parseInt(key)]?.mandatory
						? 'overwrite'
						: action
					: action,
		};
	}, {});
};

const getActionsDifference = (
	actionsA: Record<string, AttributeAction> = {},
	actionsB: Record<string, AttributeAction> = {},
) => {
	const changed: {
		id: string;
		oldAction?: AttributeAction | null;
		newAction?: AttributeAction | null;
	}[] = [];

	for (const idA in actionsA) {
		if (actionsA[idA] !== actionsB[idA]) {
			// changed/removed
			changed.push({
				id: idA,
				oldAction: actionsA[idA],
				newAction: actionsB[idA],
			});
		}
	}

	for (const idB in actionsB) {
		if (actionsA[idB] == null) {
			// added
			changed.push({
				id: idB,
				oldAction: null,
				newAction: actionsB[idB],
			});
		}
	}

	return changed;
};

export type UseAttributeFormActionsParams = {
	form: UseFormReturn<Record<string, AttributeValue>>;
	onSubmit: (
		data: Record<string, AttributeValue>,
		actions: Record<string, AttributeAction>,
		changes: Partial<Readonly<{[x: string]: boolean | boolean[]}>>,
	) => Promise<any>;
};

export const useAttributeFormActions = ({
	form,
	onSubmit,
}: UseAttributeFormActionsParams) => {
	const {data, getByIdsAsObject} = useAttributeProvider();

	// console.log('form dirty', form.formState.isDirty, form.formState.dirtyFields);
	// console.log('form valid', form.formState.isValid);
	// console.log('form error', form.formState.errors);
	// console.log('form submitting', form.formState.isSubmitting);
	// console.log('form submitted', form.formState.isSubmitted);
	// console.log('form submitCount', form.formState.submitCount);
	// console.log('form submit successful', form.formState.isSubmitSuccessful);
	// console.log('form.formState', form.formState);

	/**
	 * submit handler wrapper\
	 * will provide the form data, actions, and dirty fields
	 */
	const handleSubmit = useConstCallback(
		form.handleSubmit(async (data, event) => {
			// console.log('submit', data, getActions(), form.formState.dirtyFields);
			// this is where we could manipulate data before passing along
			return onSubmit(data, getActions(), form.formState.dirtyFields).then(
				(result) => {
					setActions(
						Object.entries(getActions()).reduce(
							(accum, [id, action]) => ({
								[id]: action === 'append' ? 'append' : 'overwrite',
							}),
							{},
						),
					);
					return result;
				},
				(error) => {
					// probably cancelled
					// console.log('error', error);
				},
			);
		}),
	);

	// actions are used in forms and can contain quite a few components
	// virtualizing form is... well, not really a thing because the varying
	// sizes and validation errors popping in and out, text fields growing in size
	// tags etc etc
	// so, to that end we need to mitigate the amount of rendering updating an action causes
	// we do not want every field to re-render when we change just one field's action
	// for this we can use jotai atoms
	// you could just drill this object down to attribute sets -> attribute set -> individual action to attribute
	// you would need to also drill down the `setAction` -
	const actionsAtom = useMemo(() => {
		const actions = atom(
			{} as Record<string, AttributeAction>,
			(get, set, newActions: Record<string, AttributeAction>) => {
				const currentActions = get(actions);

				// get templates involved so we can enforce mandatory or anything else
				const attributeTemplates = getByIdsAsObject(
					Object.keys(newActions).map((id) => parseInt(id)),
				);

				const validNewActions = getValidActions(newActions, attributeTemplates);

				getActionsDifference(currentActions, validNewActions).forEach((d) => {
					// clearing the value
					if (d.newAction === 'clear') {
						form.setValue(d.id, null, {
							// without this, the lib will not consider it dirty if the original value was `undefined`
							// null !== undefined but whatcha gonna do?!
							shouldDirty: true,
							shouldTouch: true,
						});
						return;
					}

					// we were just in a cleared state
					// and we are not being brought out of it via user input
					if (d.oldAction === 'clear' && form.getValues()[d.id] === null) {
						form.resetField(d.id);
						return;
					}
				});

				// set atom
				set(actions, validNewActions);
			},
		);
		return actions;
	}, [data]);

	/** get action `atom` */
	const getActionAtom = useCallback(
		(fieldName: string) =>
			focusAtom(actionsAtom, (optic) => optic.prop(fieldName.toString())),
		[actionsAtom],
	);

	/** get action */
	const getAction = useConstCallback(
		(fieldName: string) => getActions()[fieldName],
	);

	/** get actions */
	const getActions = useAtomCallback(
		useConstCallback((get) => get(actionsAtom)),
	);

	/** set action */
	const setAction = useConstCallback(
		(attributeId: number, action: AttributeAction) => {
			setActions({...getActions(), [attributeId]: action});
		},
	);

	/** set actions */
	const setActions = useSetAtom(actionsAtom);

	/** toggle single field action */
	const toggleAction = useConstCallback(
		(attributeId: number, action: AttributeAction, on?: boolean) => {
			setAction(attributeId, on ? action : 'overwrite');
		},
	);

	/** toggle all field actions */
	const toggleActions = useConstCallback(
		(action: AttributeAction, on?: boolean) => {
			setActions(
				Object.keys(form.getValues()).reduce<Record<string, AttributeAction>>(
					(accum, key) => ({
						...accum,
						[key.toString()]: on ? action : 'overwrite',
					}),
					{},
				),
			);
		},
	);

	return {
		getAction,
		getActionAtom,
		getActions,
		handleSubmit,
		setAction,
		setActions,
		toggleAction,
		toggleActions,
	};
};

export default useAttributeFormActions;
